// src/Pages/ServiceDetails.jsx
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Banner from "../Components/Banner";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useServices } from "../hooks/useServices";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

// Helper: strip HTML just for meta description / SEO
const stripHtml = (html = "") =>
  html
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const ServiceDetails = () => {
  const { slug } = useParams();
  const { getServiceBySlug } = useServices();

  // Loading state
  const [loading, setLoading] = useState(true);

  // Simulate loading for better UX
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 800); // 0.8 second delay for skeleton visibility

    return () => clearTimeout(timer);
  }, []);

  // Get service using slug
  const service = getServiceBySlug(slug);

  if (!service && !loading) {
    return (
      <div className="container py-5 text-center">
        <h2>Service not found</h2>
        <p className="text-muted mb-4">
          The requested service could not be found.
        </p>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        {/* Basic SEO */}
        <title> Service Details - Sydney Sri Ayyappa Swami Centre</title>
        <meta
          name="description"
          content="Sydney Sri Ayyappa Swami Centre is a spiritual hub for Lord Ayyappa devotees, offering poojas, bhajans, festivals, and community services in Sydney."
        />
        <meta
          name="keywords"
          content="Sydney Sri Ayyappa Swami Centre, Ayyappa Temple Sydney, Lord Ayyappa Temple Australia, Hindu Temple in Sydney, Ayyappa Pooja Sydney, Swami Ayyappa Devotees Sydney, Sabarimala Ayyappa Sydney, Hindu Religious Centre Sydney, Ayyappa Bhajans Sydney, Indian Hindu Temple Sydney"
        />

        {/* Open Graph */}
        <meta property="og:title" content="Sydney Sri Ayyappa Swami Centre" />
        <meta
          property="og:description"
          content="Sydney Sri Ayyappa Swami Centre is a spiritual hub for Lord Ayyappa devotees, offering poojas, bhajans, festivals, and community services in Sydney."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sydneysriayyappa.com/" />

        {/* Social Links */}
        <meta
          property="og:see_also"
          content="https://www.youtube.com/@SydneySriAyyappaSwamiCentre"
        />
        <meta
          property="og:see_also"
          content="https://www.instagram.com/sydneysriayyappa/"
        />
        <meta
          property="og:see_also"
          content="https://www.facebook.com/sydneyayyappa"
        />

        {/* Facebook  */}

        <meta
          property="fb:admins"
          content="https://www.facebook.com/sydneyayyappa"
        />

        {/* Instagram */}
        <meta
          name="instagram:title"
          content="Sydney Sri Ayyappa Swami Centre"
        />
        <meta
          name="instagram:description"
          content="Sydney Sri Ayyappa Swami Centre is a spiritual hub for Lord Ayyappa devotees, offering poojas, bhajans, festivals, and community services in Sydney."
        />

        <meta name="instagram:site" content="@sydneysriayyappa" />
      </Helmet>

      <div className="container-fluid p-0">
        {/* Banner Section - Show skeleton if loading */}
        {loading ? (
          <div className="position-relative">
            <Skeleton height={400} width="100%" />
            <div className="position-absolute top-50 start-50 translate-middle text-center w-100 px-3">
              <Skeleton
                height={60}
                width={300}
                style={{ marginBottom: "20px" }}
              />
              <Skeleton
                height={20}
                width={200}
                style={{ marginBottom: "10px" }}
              />
              <Skeleton height={20} width={150} />
            </div>
          </div>
        ) : (
          <Banner bannerData={service} />
        )}

        {/* Content Section with responsive layout */}
        <section>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="about-section bg-white py-4 py-md-5">
              <div className="container">
                {/* Mobile View */}
                <div className="d-block d-lg-none">
                  {/* Mobile Image - Skeleton while loading */}
                  <motion.div
                    className="mb-4"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                  >
                    {loading ? (
                      <Skeleton
                        height={250}
                        width="100%"
                        style={{ borderRadius: "10px" }}
                      />
                    ) : (
                      <div className="image-container rounded overflow-hidden shadow">
                        <img
                          src={service.image}
                          alt={service.title}
                          className="img-fluid w-100 object-fit-cover"
                          onError={(e) => {
                            e.target.src =
                              "https://via.placeholder.com/600x250/f8f9fa/6c757d?text=Service+Image";
                          }}
                        />
                      </div>
                    )}
                  </motion.div>

                  {/* Mobile Content */}
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
                  >
                    {/* First Paragraph: Short Description - Skeleton while loading */}
                    {loading ? (
                      <div className="mb-3 px-3">
                        <Skeleton
                          count={3}
                          height={20}
                          width="100%"
                          style={{ marginBottom: "10px" }}
                        />
                      </div>
                    ) : (
                      <motion.p
                        className="mb-3 lh-base fs-6 px-3"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{
                          duration: 0.6,
                          delay: 0.4,
                          ease: "easeOut",
                        }}
                      >
                        {service.description}
                      </motion.p>
                    )}

                    {/* Second Paragraph: Long Description/CKEditor content - Skeleton while loading */}
                    {loading ? (
                      <div className="px-3">
                        <Skeleton
                          height={20}
                          width="90%"
                          style={{ marginBottom: "10px" }}
                        />
                        <Skeleton
                          height={20}
                          width="95%"
                          style={{ marginBottom: "10px" }}
                        />
                        <Skeleton
                          height={20}
                          width="85%"
                          style={{ marginBottom: "10px" }}
                        />
                        <Skeleton
                          height={20}
                          width="92%"
                          style={{ marginBottom: "10px" }}
                        />
                        <Skeleton
                          height={20}
                          width="80%"
                          style={{ marginBottom: "10px" }}
                        />
                        <Skeleton
                          height={20}
                          width="88%"
                          style={{ marginBottom: "10px" }}
                        />
                      </div>
                    ) : (
                      <motion.div
                        className="lh-base fs-6 cms-content px-3 align-justify"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{
                          duration: 0.6,
                          delay: 0.5,
                          ease: "easeOut",
                        }}
                        dangerouslySetInnerHTML={{
                          __html: service.description1 || "",
                        }}
                      />
                    )}
                  </motion.div>
                </div>

                {/* Desktop View: Newspaper-style layout */}
                <div className="d-none d-lg-block position-relative">
                  {/* Floating image on left for desktop - Skeleton while loading */}
                  <motion.div
                    className="float-start me-4 mb-3"
                    initial={{ opacity: 0, x: -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                    style={{
                      width: "450px",
                      shapeOutside: "margin-box",
                      shapeMargin: "1rem",
                    }}
                  >
                    {loading ? (
                      <Skeleton
                        height={350}
                        width={450}
                        style={{ borderRadius: "10px" }}
                      />
                    ) : (
                      <div className="image-container rounded overflow-hidden shadow">
                        <img
                          src={service.image}
                          alt={service.title}
                          className="img-fluid w-100 object-fit-cover"
                          style={{
                            height: "350px",
                          }}
                          onError={(e) => {
                            e.target.src =
                              "https://via.placeholder.com/400x600/f8f9fa/6c757d?text=Service+Image";
                          }}
                        />
                      </div>
                    )}
                  </motion.div>

                  {/* Wrapped text content for desktop */}
                  <motion.div
                    initial={{ opacity: 0, x: 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
                  >
                    {/* First Paragraph: Short Description - Skeleton while loading */}
                    {loading ? (
                      <div className="mb-2">
                        <Skeleton
                          height={20}
                          width="100%"
                          style={{ marginBottom: "10px" }}
                        />
                        <Skeleton
                          height={20}
                          width="95%"
                          style={{ marginBottom: "10px" }}
                        />
                        <Skeleton
                          height={20}
                          width="90%"
                          style={{ marginBottom: "20px" }}
                        />
                      </div>
                    ) : (
                      <motion.p
                        className="mb-2 lh-lg fs-5"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{
                          duration: 0.6,
                          delay: 0.4,
                          ease: "easeOut",
                        }}
                      >
                        {service.description}
                      </motion.p>
                    )}

                    {/* Second Paragraph: Long Description/CKEditor content - Skeleton while loading */}
                    {loading ? (
                      <div>
                        <Skeleton
                          height={20}
                          width="100%"
                          style={{ marginBottom: "15px" }}
                        />
                        <Skeleton
                          height={20}
                          width="95%"
                          style={{ marginBottom: "15px" }}
                        />
                        <Skeleton
                          height={20}
                          width="98%"
                          style={{ marginBottom: "15px" }}
                        />
                        <Skeleton
                          height={20}
                          width="92%"
                          style={{ marginBottom: "15px" }}
                        />
                        <Skeleton
                          height={20}
                          width="96%"
                          style={{ marginBottom: "15px" }}
                        />
                        <Skeleton
                          height={20}
                          width="90%"
                          style={{ marginBottom: "15px" }}
                        />
                        <Skeleton
                          height={20}
                          width="94%"
                          style={{ marginBottom: "15px" }}
                        />
                        <Skeleton
                          height={20}
                          width="85%"
                          style={{ marginBottom: "15px" }}
                        />
                      </div>
                    ) : (
                      <motion.div
                        className="lh-lg fs-5 cms-content align-justify text-break"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{
                          duration: 0.6,
                          delay: 0.5,
                          ease: "easeOut",
                        }}
                        dangerouslySetInnerHTML={{
                          __html: service.description1 || "",
                        }}
                      />
                    )}
                  </motion.div>

                  {/* Clear float for desktop design */}
                  <div className="clearfix"></div>
                </div>
              </div>
            </div>
          </motion.div>
        </section>
      </div>
    </>
  );
};

export default ServiceDetails;
